// src/hooks/useGlobalUnreadCount.js
import { useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { io } from 'socket.io-client';
import { BASE_URL, URL } from '../components/BaseUrl';
import { decryptToken } from '../Utlis/Encryption';
import {
  updateSuperAdminCount,
  updateCompanyAdminCount,
  setUnreadCounts,
} from '../Redux/Slices/MessageCountSlice';

/**
 * Global hook to manage unread message counts across the app.
 * - Fetches initial counts via API on mount
 * - Maintains Socket.IO connections for real-time updates
 */
const useGlobalUnreadCount = () => {
  const dispatch = useDispatch();
  const socketRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Get token from localStorage
  const getToken = useCallback(() => {
    const rawEncryptedToken = localStorage.getItem('token');
    if (!rawEncryptedToken) return null;
    try {
      return decryptToken(rawEncryptedToken);
    } catch (e) {
      console.error('Failed to decrypt token:', e);
      return null;
    }
  }, []);

  // Fetch unread counts from API
  const fetchUnreadCounts = useCallback(async () => {
    const token = getToken();
    if (!token) {
      console.log('📊 No token found, skipping unread count fetch');
      return;
    }

    console.log('📊 Fetching global unread counts...');

    try {
      // Fetch from the detailed unread counts endpoint
      const res = await fetch(`${BASE_URL}/messages/unread-counts/detailed`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        console.log('📊 Unread counts response:', data);

        if (data.data) {
          const totalUnread = data.data.totalUnread || 0;
          const threadCounts = data.data.threadCounts || [];

          // Calculate role-based counts
          let companyAdminCount = 0;
          let superAdminCount = 0;
          const threads = {};

          threadCounts.forEach((item) => {
            threads[item.threadId] = item.unreadCount || 0;
            
            // Sum by role
            if (item.role === 'company_admin') {
              companyAdminCount += item.unreadCount || 0;
            } else if (item.role === 'super_admin' || item.role === 'superadmin') {
              superAdminCount += item.unreadCount || 0;
            }
          });

          // If no role-based breakdown, use total for super admin
          if (companyAdminCount === 0 && superAdminCount === 0 && totalUnread > 0) {
            superAdminCount = totalUnread;
          }

          // Dispatch to Redux
          dispatch(setUnreadCounts({
            total: totalUnread,
            threads: threads,
            superAdmin: superAdminCount,
            companyAdmin: companyAdminCount,
            roleBased: {
              company_admin: companyAdminCount,
              super_admin: superAdminCount,
            },
          }));

          // Also update the individual counts for sidebar
          dispatch(updateCompanyAdminCount({ type: 'set', count: companyAdminCount }));
          dispatch(updateSuperAdminCount({ type: 'set', count: superAdminCount }));

          console.log('📊 Global unread counts dispatched:', { 
            totalUnread, 
            companyAdminCount, 
            superAdminCount 
          });
        }
      } else {
        console.log('📊 Unread counts API returned:', res.status);
      }
    } catch (err) {
      console.error('❌ Failed to fetch global unread counts:', err);
    }
  }, [dispatch, getToken]);

  // Initialize socket connection
  const initSocket = useCallback(() => {
    const token = getToken();
    if (!token) return;

    // Use URL for socket connection
    const socketUrl = URL;

    if (!socketRef.current) {
      console.log('🔌 Initializing global socket at:', socketUrl);
      socketRef.current = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      socketRef.current.on('connect', () => {
        console.log('🔌 Global socket connected');
      });

      // Direct update from server - total count
      socketRef.current.on('total_unread_count_updated', (data) => {
        console.log('📩 total_unread_count_updated:', data);
        if (data) {
          const totalUnread = data.totalUnread || 0;
          const threadCounts = data.threadCounts || [];

          // Calculate role-based counts
          let companyAdminCount = 0;
          let superAdminCount = 0;
          const threads = {};

          threadCounts.forEach((item) => {
            threads[item.threadId] = item.unreadCount || 0;
            if (item.role === 'company_admin') {
              companyAdminCount += item.unreadCount || 0;
            } else if (item.role === 'super_admin' || item.role === 'superadmin') {
              superAdminCount += item.unreadCount || 0;
            }
          });

          if (companyAdminCount === 0 && superAdminCount === 0 && totalUnread > 0) {
            superAdminCount = totalUnread;
          }

          dispatch(setUnreadCounts({
            total: totalUnread,
            threads: threads,
            superAdmin: superAdminCount,
            companyAdmin: companyAdminCount,
          }));
          dispatch(updateCompanyAdminCount({ type: 'set', count: companyAdminCount }));
          dispatch(updateSuperAdminCount({ type: 'set', count: superAdminCount }));
        }
      });

      // Thread-specific update
      socketRef.current.on('unread_count_updated', (data) => {
        console.log('📩 unread_count_updated:', data);
        // Refetch to get accurate counts
        setTimeout(fetchUnreadCounts, 500);
      });

      // New message received - refetch counts
      socketRef.current.on('new_message', (data) => {
        console.log('📩 new_message received:', data);
        setTimeout(fetchUnreadCounts, 500);
      });

      // Messages marked as read - refetch counts
      socketRef.current.on('messages_read', (data) => {
        console.log('📩 messages_read:', data);
        setTimeout(fetchUnreadCounts, 500);
      });

      socketRef.current.on('disconnect', () => {
        console.log('🔌 Global socket disconnected');
      });

      socketRef.current.on('connect_error', (error) => {
        console.log('🔌 Global socket connection error:', error.message);
      });
    }
  }, [dispatch, getToken, fetchUnreadCounts]);

  // Cleanup socket
  const cleanupSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  // Handle visibility change - refetch when tab becomes visible
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible' && getToken()) {
      console.log('👁️ Tab became visible, refreshing unread counts...');
      fetchUnreadCounts();
    }
  }, [fetchUnreadCounts, getToken]);

  // Initialize on mount
  useEffect(() => {
    const token = getToken();
    if (!token || isInitializedRef.current) return;

    console.log('🚀 Initializing global unread count hook...');
    isInitializedRef.current = true;

    // Fetch initial counts
    fetchUnreadCounts();

    // Initialize socket connection
    initSocket();

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Periodic refresh every 60 seconds
    const intervalId = setInterval(() => {
      if (getToken()) {
        fetchUnreadCounts();
      }
    }, 60000);

    return () => {
      cleanupSocket();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
      isInitializedRef.current = false;
    };
  }, [getToken, fetchUnreadCounts, initSocket, cleanupSocket, handleVisibilityChange]);

  return {
    refetch: fetchUnreadCounts,
  };
};

export default useGlobalUnreadCount;
